/** Accessible learner slider. Wraps React Aria Slider with learner tokens. */
import type { CSSProperties, ReactNode } from 'react';
import {
  Label,
  Slider as AriaSlider,
  SliderOutput,
  SliderThumb,
  SliderTrack,
  Text,
  type SliderProps as AriaSliderProps,
} from 'react-aria-components';
import { cn } from '@/foundation/utils/cn';

export interface SliderProps extends Omit<
  AriaSliderProps<number>,
  'children' | 'className' | 'style'
> {
  className?: string;
  description?: ReactNode;
  label?: ReactNode;
  output?: ReactNode;
  style?: CSSProperties;
}

export function Slider({ className, description, label, output, style, ...props }: SliderProps) {
  return (
    <AriaSlider
      {...props}
      className={cn('flex flex-col gap-2', className)}
      style={{
        color: 'var(--color-text)',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {({ state, isDisabled }) => (
        <>
          {(label || output) && (
            <div className="flex items-center justify-between gap-3">
              {label && (
                <Label
                  className="font-semibold uppercase"
                  style={{
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--text-xs)',
                    letterSpacing: '0.08em',
                  }}
                >
                  {label}
                </Label>
              )}
              <SliderOutput
                className="ml-auto tabular-nums"
                style={{
                  color: 'var(--color-text)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--weight-bold)',
                }}
              >
                {output ?? state.getThumbValue(0)}
              </SliderOutput>
            </div>
          )}

          <SliderTrack
            style={({ isHovered }) => ({
              alignItems: 'center',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              height: 28,
              opacity: isDisabled ? 0.5 : 1,
              position: 'relative',
              touchAction: 'none',
              width: '100%',
              ['--slider-track-background' as string]: isHovered
                ? 'var(--color-border-strong)'
                : 'var(--color-border)',
            })}
          >
            {({ state: trackState }) => {
              const percent = trackState.getThumbPercent(0) * 100;
              return (
                <>
                  <div
                    style={{
                      background: 'var(--slider-track-background)',
                      borderRadius: 'var(--radius-full)',
                      height: 8,
                      left: 0,
                      position: 'absolute',
                      right: 0,
                    }}
                  />
                  <div
                    style={{
                      background: 'var(--color-accent)',
                      borderRadius: 'var(--radius-full)',
                      height: 8,
                      left: 0,
                      position: 'absolute',
                      width: `${percent}%`,
                    }}
                  />
                  <SliderThumb
                    index={0}
                    style={({ isDragging, isFocusVisible, state: thumbState }) => ({
                      background: 'var(--color-surface-solid)',
                      border: '2px solid var(--color-accent)',
                      borderRadius: 'var(--radius-full)',
                      boxShadow: isFocusVisible
                        ? '0 0 0 3px var(--color-accent-subtle), var(--shadow-sm)'
                        : isDragging
                          ? 'var(--shadow-md)'
                          : 'var(--shadow-sm)',
                      height: 20,
                      left: `${thumbState.getThumbPercent(0) * 100}%`,
                      outline: 'none',
                      position: 'absolute',
                      top: '50%',
                      transform: `translate(-50%, -50%) scale(${isDragging ? 1.08 : 1})`,
                      transition:
                        'box-shadow var(--motion-fast) var(--motion-ease-out), transform var(--motion-fast) var(--motion-ease-out)',
                      width: 20,
                    })}
                  />
                </>
              );
            }}
          </SliderTrack>

          {description && (
            <Text
              slot="description"
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--text-xs)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              {description}
            </Text>
          )}
        </>
      )}
    </AriaSlider>
  );
}
